import ImageIcon from "@mui/icons-material/ImageOutlined";
import Paper, { type PaperProps } from "@mui/material/Paper";
import { CustomModal } from "core/components/CustomModal";
import { useState } from "react";

type ImagePreviewProps = {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  sx?: PaperProps["sx"];
  checkeredBackground?: boolean;
};

export function EmptyImagePreview({
  width = 300,
  height = 200,
  sx,
}: Omit<ImagePreviewProps, "src" | "alt">) {
  return (
    <Paper
      data-testid="image-preview_empty"
      variant="outlined"
      sx={{
        width,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      <ImageIcon sx={{ fontSize: 48, color: "text.disabled" }} />
    </Paper>
  );
}

export function ImagePreview({
  src,
  alt,
  width = 300,
  height = 200,
  sx,
  checkeredBackground = false,
}: ImagePreviewProps) {
  const [open, setOpen] = useState(false);
  if (!src) {
    return <EmptyImagePreview width={width} height={height} sx={sx} />;
  }

  return (
    <>
      <Paper
        onClick={() => {
          setOpen(true);
        }}
        data-testid="image-preview_active"
        variant="outlined"
        sx={{
          width,
          height,
          overflow: "hidden",
          transition: "transform 0.1s ease-in-out",
          backgroundColor: "transparent",
          "&:hover": {
            cursor: "pointer",
            transform: "scale(1.02)",
          },
          ...sx,
          ...(checkeredBackground && {
            backgroundImage:
              "repeating-conic-gradient(#ccc 0% 25%, #eee 0% 50%)",
            backgroundSize: "40px 40px",
          }),
        }}
      >
        {/** biome-ignore lint/performance/noImgElement: required */}
        <img
          style={{
            objectFit: "contain",
          }}
          src={src}
          alt={alt}
          width="100%"
          height="100%"
        />
      </Paper>
      <CustomModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Paper
          sx={{
            maxWidth: "80vw",
            minWidth: "min(800px,calc(100vw - 56px))",
            maxHeight: "90vh",
            p: 2,
            overflow: "auto",
            ...(checkeredBackground && {
              backgroundImage:
                "repeating-conic-gradient(#ccc 0% 25%, #eee 0% 50%)",
              backgroundSize: "40px 40px",
            }),
          }}
        >
          {/** biome-ignore lint/performance/noImgElement: required */}
          <img
            style={{
              objectFit: "contain",
            }}
            src={src}
            alt={alt}
            width="100%"
            height="100%"
          />
        </Paper>
      </CustomModal>
    </>
  );
}
